import { toast } from "sonner";
import { Loader2, Reply, Forward, ArrowLeft, Paperclip, MailOpen } from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";

const STYLE = "<base target=\"_blank\"><style>body{font-family:Arial,sans-serif;font-size:14px;color:#0f172a;margin:0;padding:8px;word-wrap:break-word}img{max-width:100%;height:auto}</style>";
const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export const MailReader = ({ msg, loading, folder, onReply, onForward, onBack }) => {
  if (loading) return <div className="flex-1 flex justify-center items-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  if (!msg) return <div className="flex-1 flex flex-col items-center justify-center text-sm text-muted-foreground gap-2"><MailOpen className="w-8 h-8 opacity-40" />Seleziona un'email da leggere</div>;

  const download = async (a) => {
    try {
      const r = await api.get(`/mail/messages/${msg.uid}/attachments/${a.idx}`, { params: { folder }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = a.filename; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch { toast.error("Download dell'allegato non riuscito"); }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="mail-reader">
      <div className="px-4 sm:px-5 py-3 border-b border-border space-y-2">
        <div className="flex items-start gap-2">
          <Button variant="ghost" size="icon" className="lg:hidden -ml-2 shrink-0" onClick={onBack} data-testid="mail-back-button"><ArrowLeft className="w-4 h-4" /></Button>
          <h2 className="font-heading font-bold text-lg leading-snug flex-1 min-w-0 break-words" data-no-translate data-testid="mail-subject">{msg.subject}</h2>
          <div className="flex gap-1 shrink-0">
            <Button size="sm" variant="outline" onClick={onReply} data-testid="mail-reply-button"><Reply className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Rispondi</span></Button>
            <Button size="sm" variant="ghost" onClick={onForward} data-testid="mail-forward-button"><Forward className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Inoltra</span></Button>
          </div>
        </div>
        <div className="text-xs text-muted-foreground space-y-0.5" data-no-translate>
          <div><b className="text-foreground">{msg.from_name}</b> &lt;{msg.from_email}&gt; · {fmtDateTime(msg.date)}</div>
          <div className="truncate">A: {msg.to}{msg.cc ? ` · Cc: ${msg.cc}` : ""}</div>
        </div>
        {msg.attachments?.length > 0 && (
          <div className="flex gap-2 flex-wrap pt-1">
            {msg.attachments.map((a) => (
              <button key={a.idx} type="button" onClick={() => download(a)} className="text-xs rounded-full border border-border px-3 py-1 hover:bg-secondary flex items-center gap-1.5" data-testid={`mail-attachment-${a.idx}`} data-no-translate>
                <Paperclip className="w-3.5 h-3.5" /> {a.filename} <span className="text-muted-foreground">{kb(a.size)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-auto p-3 sm:p-4" data-no-translate>
        {msg.html
          ? <iframe title="email" sandbox="allow-popups allow-popups-to-escape-sandbox" srcDoc={STYLE + msg.html} className="w-full h-full min-h-[420px] bg-white rounded-lg border border-border" data-testid="mail-html-body" />
          : <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed" data-testid="mail-text-body">{msg.text}</pre>}
      </div>
    </div>
  );
};

export default MailReader;
